import { Input, Select } from "antd";
import { useTranslation } from "react-i18next";
import { ETF_METHODOLOGIES } from "../../../api/services/etf-screener.type";
import { Button } from "../../components/CustomAntdComponents/Button";
import styles from "../StockScreener/stockScreener.module.css";
import {
  ETF_AUM_BANDS,
  ETF_COVERAGE_BANDS,
  ETF_DOMICILE_VALUES,
  ETF_EXPENSE_BANDS,
  ETF_STATUS_VALUES,
  ETF_VENUE_VALUES,
  type EtfFilterValues,
} from "./etfFilters";
import etfStyles from "./etfScreener.module.css";

type Props = {
  values: EtfFilterValues;
  onChange: (values: EtfFilterValues) => void;
  onApply: () => void;
  onClear: () => void;
  onShare?: () => void;
  isApplying?: boolean;
};

const STATUS_LABEL_KEY: Record<string, string> = {
  HALAL: "halal",
  DOUBTFUL: "doubtful",
  "NOT HALAL": "notHalal",
  UNQUALIFIED: "notRated",
};

const AUM_LABEL: Record<string, string> = {
  "10000000": "10M",
  "100000000": "100M",
  "1000000000": "1B",
  "10000000000": "10B",
};

/** Same card, grid and buttons as the stock screener's filter panel. */
export function EtfScreenerFilters({
  values,
  onChange,
  onApply,
  onClear,
  onShare,
  isApplying,
}: Props) {
  const { t } = useTranslation();
  const p = "pages.etfScreener.filters.";
  const anyLabel = t(`${p}any`);
  const set = <K extends keyof EtfFilterValues>(key: K, value: EtfFilterValues[K]) =>
    onChange({ ...values, [key]: value });

  const select = (
    field: keyof EtfFilterValues,
    options: { value: string; label: string }[],
    allowClear = true,
  ) => (
    <div key={field} className={styles.filterRow}>
      <label className={styles.filterLabel} htmlFor={`etf-${field}`}>
        {t(`${p}fields.${field}`)}
      </label>
      <div className={styles.filterSelectWrap}>
        <Select
          id={`etf-${field}`}
          size="small"
          className={styles.screenerSelect}
          popupClassName={styles.screenerSelectDropdown}
          getPopupContainer={() => document.body}
          virtual={false}
          value={values[field] || undefined}
          placeholder={anyLabel}
          allowClear={allowClear}
          onChange={(value) => set(field, ((value as string) ?? "") as never)}
          options={options}
        />
      </div>
    </div>
  );

  const textField = (field: "issuer" | "q", placeholderKey: string) => (
    <div key={field} className={styles.filterRow}>
      <label className={styles.filterLabel} htmlFor={`etf-${field}`}>
        {t(`${p}fields.${field}`)}
      </label>
      <div className={styles.filterSelectWrap}>
        <Input
          id={`etf-${field}`}
          size="small"
          className={etfStyles.textInput}
          value={values[field]}
          maxLength={100}
          allowClear
          placeholder={t(`${p}${placeholderKey}`)}
          onChange={(event) => set(field, event.target.value)}
          onPressEnter={onApply}
        />
      </div>
    </div>
  );

  return (
    <div className={styles.filtersCard}>
      <div className={styles.filterGroup}>
        <div className={styles.filterGrid}>
          {select(
            "status",
            ETF_STATUS_VALUES.map((value) => ({
              value,
              label: t(`pages.etfScreener.verdict.${STATUS_LABEL_KEY[value]}`),
            })),
          )}
          {select(
            "methodology",
            ETF_METHODOLOGIES.map((value) => ({
              value,
              label: t(`${p}methodologies.${value}`),
            })),
            false,
          )}
          {select(
            "venue",
            ETF_VENUE_VALUES.map((value) => ({
              value,
              label: t(`pages.etfScreener.venueNames.${value}`),
            })),
          )}
          {select(
            "domicile",
            ETF_DOMICILE_VALUES.map((value) => ({
              value,
              label: t(`${p}domiciles.${value}`),
            })),
          )}
          {select(
            "coverage",
            ETF_COVERAGE_BANDS.map((value) => ({ value, label: `≥ ${value}%` })),
          )}
          {select(
            "expense",
            ETF_EXPENSE_BANDS.map((value) => ({
              value,
              label: `≤ ${Number(value).toFixed(2)}%`,
            })),
          )}
          {select(
            "aum",
            ETF_AUM_BANDS.map((value) => ({ value, label: `≥ ${AUM_LABEL[value]}` })),
          )}
          {textField("issuer", "issuerPlaceholder")}
          {textField("q", "searchPlaceholder")}
        </div>
        <ul className={etfStyles.notes}>
          <li>{t(`${p}notes.status`)}</li>
          <li>{t(`${p}notes.venue`)}</li>
          <li>{t(`${p}notes.expenseAum`)}</li>
        </ul>
      </div>

      <div className={styles.filterActions}>
        <span />
        <div className={styles.filterActionButtons}>
          <Button size="small" onClick={onClear}>
            {t(`${p}clear`)}
          </Button>
          <Button size="small" type="primary" loading={isApplying} onClick={onApply}>
            {t(`${p}apply`)}
          </Button>
          {onShare && (
            <Button size="small" onClick={onShare}>
              {t(`${p}share`)}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
